import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { LogoIcon } from "@/components/Logo";

const FALLBACK_WA = "919920223329";
const QUICK = [
  "How do I apply?",
  "What investment is needed?",
  "Do I get monopoly rights?",
  "Which products can I sell?",
];

function WhatsappIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export default function FloatingWidgets() {
  const [open, setOpen] = useState(false);
  const [waNumber, setWaNumber] = useState(FALLBACK_WA);
  const [sessionId] = useState(() => {
    const key = "saans_chat_session";
    let s = localStorage.getItem(key);
    if (!s) { s = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(key, s); }
    return s;
  });
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Namaste! I'm the Saans Wellness franchise assistant. Ask me about our Ayurvedic & Nutraceutical PCD franchise — investment, monopoly rights, products or how to apply." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => { if (data?.whatsapp_number) setWaNumber(data.whatsapp_number); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent("Hi Saans Wellness, I'm interested in your PCD franchise. Please share details.")}`;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble right now — please tap 'Chat on WhatsApp' below or use the enquiry form, and our team will help you directly." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher stack */}
      <div className="fixed bottom-5 right-5 z-[70] flex flex-col items-end gap-3">
        <a href={waLink} target="_blank" rel="noreferrer" data-testid="whatsapp-button"
          className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-lg transition-transform hover:scale-105">
          <WhatsappIcon size={22} />
          <span className="hidden text-sm font-semibold sm:inline">Chat on WhatsApp</span>
        </a>

        <button onClick={() => setOpen((o) => !o)} data-testid="chat-toggle"
          className="grid h-14 w-14 place-items-center rounded-full bg-[#d46a1d] text-white shadow-lg transition-transform hover:scale-105">
          {open ? <X size={24} /> : <MessageCircle size={24} />}
        </button>
      </div>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[70] flex h-[70vh] max-h-[560px] w-[92vw] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-[#e7e0d6] bg-white shadow-2xl"
            data-testid="chat-panel">
            {/* header */}
            <div className="flex items-center gap-3 bg-[#1f5d3a] px-4 py-3.5 text-white">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white"><LogoIcon size={24} arc="#ffffff" sprout="#ffffff" /></span>
              <div className="flex-1">
                <div className="text-sm font-semibold">Saans Assistant</div>
                <div className="flex items-center gap-1 text-[11px] text-[#bfe0cd]"><Sparkles size={11} /> PCD Franchise help</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-white/80 hover:text-white"><X size={18} /></button>
            </div>

            {/* messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#faf8f4] p-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "rounded-br-sm bg-[#d46a1d] text-white" : "rounded-bl-sm border border-[#e7e0d6] bg-white text-[#2b2a27]"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl-sm border border-[#e7e0d6] bg-white px-3.5 py-2.5 text-sm text-[#9a9488]">
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#c7bca9] [animation-delay:-0.2s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#c7bca9] [animation-delay:-0.1s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#c7bca9]" />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* quick chips (only before first user turn) */}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 border-t border-[#f0ebe3] bg-white px-3 py-2.5">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid={`chat-quick-${q.slice(0,6)}`}
                    className="rounded-full border border-[#e7e0d6] bg-[#f9f8f5] px-3 py-1.5 text-xs font-medium text-[#4a4a48] hover:border-[#d46a1d] hover:text-[#d46a1d]">
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* input */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-[#e7e0d6] bg-white p-3">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about the franchise…"
                data-testid="chat-input"
                className="flex-1 rounded-full border border-[#e7e0d6] bg-[#faf8f4] px-4 py-2.5 text-sm text-[#202124] outline-none focus:border-[#d46a1d]" />
              <button type="submit" disabled={sending || !input.trim()} data-testid="chat-send"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#d46a1d] text-white transition-transform hover:scale-105 disabled:opacity-50">
                <Send size={17} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
